import React from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Desktop form vocabulary: a stacked label above a bordered control, with an
 * optional hint or error below. One look for every input, select and
 * textarea on the desktop surfaces, so a control never reads as static text.
 *
 * Contrast: labels use --text-secondary (6.3:1 on white); placeholders use
 * --text-secondary too, since --text-tertiary fails AA on the alt surface.
 */

export const controlClass =
  'w-full h-[36px] rounded-ios-control border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] outline-none transition-colors duration-150 hover:border-[var(--text-tertiary)] focus:border-accent-500 focus:ring-2 focus:ring-[var(--ring-accent)] disabled:opacity-50 disabled:cursor-not-allowed';

export const controlErrorClass = 'border-red-500 focus:border-red-500 focus:ring-red-500/20';

interface FieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
  className?: string;
  /** Right-aligned content in the label row, e.g. an AI assist link. */
  labelAction?: React.ReactNode;
}

export function Field({ label, htmlFor, required, hint, error, children, className = '', labelAction }: FieldProps) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label htmlFor={htmlFor} className="text-[13px] font-medium text-[var(--text-secondary)]">
          {label}
          {required && (
            <span className="ml-0.5 text-red-700 dark:text-red-300" aria-hidden="true">*</span>
          )}
        </label>
        {labelAction}
      </div>
      {children}
      {error ? (
        <p role="alert" className="mt-1 text-[13px] text-red-700 dark:text-red-300">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[13px] text-[var(--text-secondary)]">{hint}</p>
      ) : null}
    </div>
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className = '', invalid, ...rest }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={`${controlClass} ${invalid ? controlErrorClass : ''} ${className}`}
        {...rest}
      />
    );
  }
);

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className = '', ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        className={`${controlClass} h-auto min-h-[96px] py-2 leading-relaxed resize-y ${className}`}
        {...rest}
      />
    );
  }
);

/** Native select with a visible chevron and the shared control frame. */
export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(
  function Select({ className = '', invalid, children, ...rest }, ref) {
    return (
      <div className={`relative ${className}`}>
        <select
          ref={ref}
          aria-invalid={invalid || undefined}
          className={`${controlClass} ${invalid ? controlErrorClass : ''} appearance-none pr-9 cursor-pointer`}
          {...rest}
        >
          {children}
        </select>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
        />
      </div>
    );
  }
);
